import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, Navigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import GuestbookAnalytics from '../components/Dashboard/GuestbookAnalytics';
import ModerationQueue from '../components/Dashboard/ModerationQueue';
import AdminWebinarTab from '../components/Dashboard/AdminWebinarTab';
import MerchantManagement from '../components/Dashboard/MerchantManagement';
import AppointmentQueue from '../components/Dashboard/AppointmentQueue';
import PaymentSettings from '../components/Dashboard/PaymentSettings';
import ContentModerationSettings from '../components/Dashboard/ContentModerationSettings';
import MemorialDefaults from '../components/Dashboard/MemorialDefaults';
import WebinarIntegrations from '../components/Dashboard/WebinarIntegrations';
import StreamingIntegrations from '../components/Dashboard/StreamingIntegrations';
import SuperAdminPanel from '../components/Dashboard/SuperAdminPanel';
import UserManagement from '../components/Dashboard/UserManagement';
import AIProviderSettings from '../components/Dashboard/AIProviderSettings';
import AIContentGeneration from '../components/Dashboard/AIContentGeneration';
import MonetizationTab from '../components/Dashboard/MonetizationTab';
import CampaignAuditTab from '../components/Dashboard/CampaignAuditTab';
import AudioFileManager from '../components/Dashboard/AudioFileManager';
import PricingSettings from '../components/Admin/PricingSettings';
import PlatformBannerSettings from '../components/Dashboard/PlatformBannerSettings';
import { useAuth } from '../context/AuthContext';

const {
  FiArrowLeft, FiSettings, FiBarChart2, FiShield,
  FiVideo, FiShoppingBag, FiCalendar,
  FiGlobe, FiCreditCard, FiFilter, FiLayout,
  FiLink, FiRadio, FiCrown, FiUsers, FiCpu, FiDollarSign,
  FiMusic, FiHeart, FiBookOpen, FiPackage, FiBell, FiExternalLink,
} = FiIcons;

const CreatorAdmin = () => {
  const [activeTab, setActiveTab] = useState('analytics');
  const { permissions } = useAuth();

  if (!permissions?.isGlobalAdmin?.()) {
    return <Navigate to="/user/dashboard" replace />;
  }

  const isSuperAdmin = permissions?.isSuperAdmin?.();

  const tabs = [
    { id: 'analytics',              label: 'Global Analytics',        icon: FiBarChart2,  superOnly: false },
    { id: 'user-management',        label: 'User Management',         icon: FiUsers,       superOnly: false },
    { id: 'webinars',               label: 'Webinar Admin',            icon: FiVideo,       superOnly: false },
    { id: 'webinar-integrations',   label: 'Meeting Integrations',    icon: FiLink,        superOnly: false },
    { id: 'streaming-integrations', label: 'Streaming Platforms',     icon: FiRadio,       superOnly: false },
    { id: 'merchants',              label: 'Merchant Management',     icon: FiShoppingBag, superOnly: false },
    { id: 'scheduling',             label: 'Legacy Scheduling',       icon: FiCalendar,    superOnly: false },
    { id: 'moderation',             label: 'Global Moderation',       icon: FiShield,      superOnly: false },
    { id: 'content-mod',            label: 'Content Moderation',      icon: FiFilter,      superOnly: false },
    { id: 'memorial-defaults',      label: 'Memorial Defaults',       icon: FiLayout,      superOnly: false },
    { id: 'payments',               label: 'Payment & Financial',     icon: FiCreditCard,  superOnly: false },
    { id: 'settings',               label: 'Platform Controls',       icon: FiSettings,    superOnly: false },
    { id: 'banner',                 label: 'Maintenance Banner',      icon: FiBell,        superOnly: false },
    { id: 'campaigns',              label: 'Campaigns Audit',         icon: FiHeart,       superOnly: true  },
    { id: 'monetization',           label: 'Monetization',            icon: FiDollarSign,  superOnly: true  },
    { id: 'pricing',                label: 'Pricing & Plans',         icon: FiPackage,     superOnly: true  },
    { id: 'audio-manager',          label: 'Soundscape Library',      icon: FiMusic,       superOnly: true  },
    { id: 'ai-content',             label: 'AI Content Generation',   icon: FiBookOpen,    superOnly: true  },
    { id: 'ai-providers',           label: 'AI Provider Settings',    icon: FiCpu,         superOnly: true  },
    { id: 'super-admin',            label: 'Manage Administrators',   icon: FiCrown,       superOnly: true  },
  ].filter(tab => !tab.superOnly || isSuperAdmin);

  return (
    <div className="pt-32 min-h-screen bg-brand-light pb-20">
      <div className="container mx-auto px-6">

        {/* Scope Banner */}
        <div className={`mb-10 border p-6 rounded-[32px] flex items-center gap-4 ${
          isSuperAdmin
            ? 'bg-gradient-to-r from-purple-900/10 to-accent-navy/10 border-purple-300/30'
            : 'bg-accent-gold/10 border-accent-gold/20'
        }`}>
          <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 shadow-lg ${
            isSuperAdmin ? 'bg-accent-gold text-accent-navy' : 'bg-accent-gold text-white'
          }`}>
            <SafeIcon icon={isSuperAdmin ? FiCrown : FiGlobe} className="w-6 h-6" />
          </div>
          <div>
            <h2 className="text-accent-navy font-serif text-lg">
              {isSuperAdmin ? 'Super Administrator Mode Active' : 'Global Administration Mode Active'}
            </h2>
            <p className="text-accent-navy/60 text-sm font-light">
              {isSuperAdmin
                ? 'You have the highest level of platform access. Manage all administrators, AI providers, AI content generation, monetization, donation campaigns, soundscape audio, and their permissions.'
                : 'You are viewing the platform from a global perspective. Actions taken here affect the entire OneFinalLegacy ecosystem.'}
            </p>
          </div>
        </div>

        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-8 mb-12">
          <div className="flex items-center gap-4">
            <Link to="/user/dashboard"
              className="p-3 bg-white rounded-2xl border border-brand-dark hover:text-accent-gold transition-colors shadow-sm">
              <SafeIcon icon={FiArrowLeft} className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-4xl font-serif text-accent-navy">Site Administration Hub</h1>
              <p className="text-text-muted text-sm">Global oversight of the OneFinalLegacy sanctuary</p>
            </div>
          </div>
          <div className="bg-accent-navy text-white px-6 py-3 rounded-2xl flex items-center gap-3 shadow-xl border border-white/10">
            <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
            <span className="text-[10px] font-black uppercase tracking-widest">System Online</span>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="flex overflow-x-auto hide-scrollbar border-b border-brand-dark mb-10 gap-1">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-5 py-4 text-xs font-black uppercase tracking-widest transition-all relative whitespace-nowrap ${
                activeTab === tab.id ? 'text-accent-gold' : 'text-text-muted hover:text-accent-navy'
              } ${tab.superOnly ? 'border-l-2 border-purple-200 ml-2' : ''}`}>
              <SafeIcon icon={tab.icon} className={`w-4 h-4 ${tab.superOnly ? 'text-accent-gold' : ''}`} />
              {tab.label}
              {tab.superOnly && (
                <span className="text-[8px] px-1.5 py-0.5 bg-accent-gold/20 text-accent-gold rounded-full font-black">SUPER</span>
              )}
              {activeTab === tab.id && (
                <motion.div layoutId="adminTab" className="absolute bottom-0 left-0 right-0 h-1 bg-accent-gold rounded-t-full" />
              )}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="min-h-[500px]">
          <AnimatePresence mode="wait">
            <motion.div key={activeTab} initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -10 }} transition={{ duration: 0.2 }}>
              {activeTab === 'analytics'              && <GuestbookAnalytics />}
              {activeTab === 'user-management'        && <UserManagement />}
              {activeTab === 'webinars'               && <AdminWebinarTab />}
              {activeTab === 'webinar-integrations'   && <WebinarIntegrations />}
              {activeTab === 'streaming-integrations' && <StreamingIntegrations />}
              {activeTab === 'merchants'              && <MerchantManagement />}
              {activeTab === 'scheduling'             && <AppointmentQueue />}
              {activeTab === 'moderation'             && <ModerationQueue />}
              {activeTab === 'content-mod'            && <ContentModerationSettings />}
              {activeTab === 'memorial-defaults'      && <MemorialDefaults />}
              {activeTab === 'payments'               && <PaymentSettings />}
              {activeTab === 'settings'               && <CreatorSettings onNavigateToBanner={() => setActiveTab('banner')} />}
              {activeTab === 'banner'                 && <PlatformBannerSettings />}
              {activeTab === 'campaigns'              && isSuperAdmin && <CampaignAuditTab />}
              {activeTab === 'monetization'           && isSuperAdmin && <MonetizationTab />}
              {activeTab === 'pricing'                && isSuperAdmin && <PricingSettings />}
              {activeTab === 'audio-manager'          && isSuperAdmin && <AudioFileManager />}
              {activeTab === 'ai-content'             && isSuperAdmin && <AIContentGeneration />}
              {activeTab === 'ai-providers'           && isSuperAdmin && <AIProviderSettings />}
              {activeTab === 'super-admin'            && isSuperAdmin && <SuperAdminPanel />}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

// ── Platform Controls (static tab — banner toggle replaced with redirect card) ─
const CreatorSettings = ({ onNavigateToBanner }) => (
  <div className="bg-white p-10 rounded-[40px] border border-brand-dark shadow-sm space-y-10">
    <div className="flex items-center gap-4 border-b border-brand-dark pb-6">
      <div className="p-3 bg-brand-light rounded-2xl text-accent-navy shadow-inner">
        <SafeIcon icon={FiSettings} className="w-6 h-6" />
      </div>
      <h3 className="text-2xl font-serif text-accent-navy">Global Platform Controls</h3>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
      {/* ── System Behavior ── */}
      <div className="space-y-6">
        <h4 className="text-[10px] font-black uppercase tracking-widest text-text-muted ml-1">System Behavior</h4>

        {/* Static toggle — not wired to banner state */}
        <ToggleSetting
          title="Auto-Approve Webinar Registrants"
          desc="Allow verified users to automatically bypass manual approval."
          active
        />

        {/* Banner redirect card — replaces the old platform_maintenance toggle */}
        <div className="flex items-start justify-between gap-4 p-6 bg-amber-50 rounded-3xl border-2 border-amber-200 hover:border-amber-300 transition-colors group">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <h4 className="font-bold text-amber-900 text-sm">Platform-wide Maintenance Banner</h4>
              <span className="text-[8px] px-2 py-0.5 bg-amber-200 text-amber-800 rounded-full font-black uppercase tracking-widest">
                Dedicated Tab
              </span>
            </div>
            <p className="text-[11px] text-amber-700 leading-relaxed font-light">
              The banner master toggle, message, theme, colour, and schedule are managed
              exclusively in the <strong className="font-bold">Maintenance Banner</strong> tab.
            </p>
          </div>
          <button
            type="button"
            onClick={onNavigateToBanner}
            className="flex items-center gap-1.5 flex-shrink-0 px-4 py-2.5 bg-amber-600 text-white rounded-2xl text-[9px] font-black uppercase tracking-widest hover:bg-amber-700 transition-all shadow-sm whitespace-nowrap"
          >
            <SafeIcon icon={FiBell} className="w-3.5 h-3.5" />
            Go to Banner
            <SafeIcon icon={FiExternalLink} className="w-3 h-3 opacity-70" />
          </button>
        </div>
      </div>

      {/* ── Financial & Merchant ── */}
      <div className="space-y-6">
        <h4 className="text-[10px] font-black uppercase tracking-widest text-text-muted ml-1">Financial & Merchant</h4>
        <ToggleSetting title="Merchant Payout Notifications" desc="Alert site administrators when a merchant requests a withdrawal." active />
        <ToggleSetting title="New Vendor Onboarding" desc="Allow new merchants to submit applications." active />
      </div>
    </div>
  </div>
);

const ToggleSetting = ({ title, desc, active = false }) => (
  <div className="flex items-center justify-between p-6 bg-brand-light rounded-3xl border border-brand-dark hover:border-accent-gold/50 transition-colors group">
    <div className="max-w-[80%]">
      <h4 className="font-bold text-accent-navy text-sm mb-1 group-hover:text-accent-gold transition-colors">{title}</h4>
      <p className="text-[11px] text-text-muted leading-relaxed font-light">{desc}</p>
    </div>
    <div className={`w-12 h-6 rounded-full relative shrink-0 cursor-pointer transition-colors ${active ? 'bg-accent-gold shadow-lg shadow-accent-gold/20' : 'bg-brand-dark'}`}>
      <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all shadow-sm ${active ? 'right-1' : 'left-1'}`} />
    </div>
  </div>
);

export default CreatorAdmin;